import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { useGetCurrentUserQuery } from '@features/auth/authApi';
import { setUser } from '@features/auth/authSlice';
import { Lock } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ROLE_LEVELS } from './RequireLevel';

interface RequireAuthProps {
	children?: React.ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
	const dispatch = useAppDispatch();
	const { user } = useAppSelector((state) => state.auth);
	const location = useLocation();

	// Try to get current user from API (uses HttpOnly cookie)
	// Only skip if we already have user data in memory
	const { data, isLoading, isFetching, isSuccess, isUninitialized } = useGetCurrentUserQuery(
		undefined,
		{
			skip: !!user, // Skip only if we already have user
		}
	);

	useEffect(() => {
		if (data?.success && data.data) {
			// Session is valid, set user from API response
			dispatch(setUser(data.data));
		}
	}, [data, dispatch]);

	// Show loading while checking auth (initial load or fetching)
	// Also show loading if query hasn't started yet (isUninitialized) and we don't have a user
	if (!user && (isLoading || isFetching || isUninitialized)) {
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '100vh',
					gap: 2,
				}}>
				<CircularProgress />
				<Typography color='text.secondary'>Проверка на сесията...</Typography>
			</Box>
		);
	}

	// User already in Redux state - authenticated
	if (user) {
		// Check if user has minimum level for admin panel (50)
		const userLevel = user.roleLevel || 0;
		if (userLevel < ROLE_LEVELS.OPERATOR) {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: '100vh',
						gap: 2,
						p: 4,
					}}>
					<Lock sx={{ fontSize: 64, color: 'error.main' }} />
					<Typography variant='h4' fontWeight={600}>
						Достъпът е отказан
					</Typography>
					<Typography color='text.secondary' textAlign='center'>
						Нямате достатъчно права за достъп до административния панел.
						<br />
						Изисква се минимум ниво {ROLE_LEVELS.OPERATOR}, вашето ниво е {userLevel}.
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						Моля, използвайте мобилното приложение за работа.
					</Typography>
					<Button
						variant='outlined'
						onClick={() => {
							// Trigger logout
							window.location.href = '/login';
						}}
						sx={{ mt: 2 }}>
						Изход
					</Button>
				</Box>
			);
		}
		return children ? <>{children}</> : <Outlet />;
	}

	// Query completed successfully with user data - wait for useEffect to set user
	if (isSuccess && data?.success && data.data) {
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '100vh',
					gap: 2,
				}}>
				<CircularProgress />
				<Typography color='text.secondary'>Зареждане...</Typography>
			</Box>
		);
	}

	// Query failed or returned no user - redirect to login
	return <Navigate to='/login' state={{ from: location }} replace />;
}
